import React, { useEffect, useMemo, useState } from 'react';

import {
  previewAssetCharge,
  removeRatePlanBand,
  removeRatePlanCreditPack,
  setRatePlanBand,
  setRatePlanCreditCost,
  setRatePlanCreditPack,
  setRatePlanEdition,
  setRatePlanEditionCopy,
  setRatePlanTrial,
  unpublishRatePlanCreditCost,
  type AssetChargeLine,
  type RatePlanDetail,
} from './api';
import { majorInput, money, parseCount, parseMajor, symbolFor } from './money';
import RatePlanAddons from './RatePlanAddons';

/**
 * The parameter editor for one rate plan version.
 *
 * Everything here writes through the admin RPCs, which refuse anything but a
 * draft — so a published version renders as plain text rather than disabled
 * inputs. A greyed-out box invites an admin to wonder what would happen if they
 * clicked; text does not.
 *
 * Every save reloads the version from the server rather than patching local
 * state. That is what makes the "modelled in its current form" banner above
 * truthful: change a band and the model run stops counting, immediately and
 * visibly, because the content hash moved.
 */

/** An input that holds its own text and commits on blur or Enter. */
function Cell({
  value,
  onCommit,
  placeholder,
  width = 92,
  disabled,
  align = 'left',
}: {
  value: string;
  onCommit: (next: string) => void;
  placeholder?: string;
  width?: number;
  disabled?: boolean;
  align?: 'left' | 'right';
}) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  if (disabled) return <span>{value || <span className="muted">—</span>}</span>;
  return (
    <input
      value={text}
      placeholder={placeholder}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => text !== value && onCommit(text)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') setText(value);
      }}
      style={{ width, textAlign: align, padding: '6px 8px', fontSize: 13 }}
    />
  );
}

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div style={{ fontWeight: 700, marginBottom: hint ? 2 : 10 }}>{title}</div>
      {hint ? <p className="sub" style={{ marginBottom: 12 }}>{hint}</p> : null}
      {children}
    </div>
  );
}

export default function RatePlanEditor({
  plan,
  onChanged,
  toast,
}: {
  plan: RatePlanDetail;
  onChanged: () => Promise<void> | void;
  toast: (message: string) => void;
}) {
  const editable = plan.status === 'draft';
  const cur = plan.currency;

  const run = async (label: string, fn: () => Promise<void>) => {
    try {
      await fn();
      await onChanged();
      toast(label);
    } catch (e) {
      toast(`Refused: ${(e as Error).message}`);
      await onChanged();
    }
  };

  // --- Editions --------------------------------------------------------------

  const saveEdition = (planKey: string, fields: Parameters<typeof setRatePlanEdition>[2], label: string) =>
    run(label, () => setRatePlanEdition(plan.id, planKey, fields));

  const editions = (
    <Section
      title="Plans"
      hint="What a customer pays before any assets, and what the plan brings with it. A plan can charge more or less than the listed asset rate; leave that at 1 to charge exactly what is above."
    >
      <div className="matrix-scroll">
        <table className="matrix">
          <thead>
            <tr>
              <th>Plan</th>
              <th style={{ textAlign: 'right' }}>A month</th>
              <th style={{ textAlign: 'right' }}>A year</th>
              <th style={{ textAlign: 'right' }} title="Months of the monthly asset price a year of assets costs on the annual plan: 10 = two months free. Empty = not offered annually.">Assets: months a year</th>
              <th style={{ textAlign: 'right' }} title="The most active assets this plan allows. Empty = no limit.">Max assets</th>
              <th style={{ textAlign: 'right' }} title="A free trial of this plan in days, card up front (Stripe). A workspace gets one. Empty = no trial.">Trial days</th>
              <th style={{ textAlign: 'right' }}>AI credits per asset</th>
              <th style={{ textAlign: 'right' }}>Asset rate ×</th>
              <th style={{ textAlign: 'center' }}>Talk to sales</th>
              <th style={{ textAlign: 'center' }}>Recommended</th>
            </tr>
          </thead>
          <tbody>
            {plan.editions.map((e) => (
              <tr key={e.plan_key}>
                <td>
                  {editable ? (
                    <Cell
                      value={e.display_name}
                      width={120}
                      onCommit={(next) => {
                        const name = next.trim();
                        if (!name) return toast('A plan needs a name');
                        return run(`${name} renamed`, () => setRatePlanEditionCopy(plan.id, e.plan_key, name, e.description));
                      }}
                    />
                  ) : (
                    <strong>{e.display_name}</strong>
                  )}{' '}
                  <span className="muted" style={{ fontSize: 11 }}>{e.plan_key}</span>
                  {!e.is_active ? <span className="pill" style={{ marginLeft: 6 }}>inactive</span> : null}
                  {editable ? (
                    <div style={{ marginTop: 4 }}>
                      <Cell
                        value={e.description ?? ''}
                        width={260}
                        placeholder="One line on who it is for (no numbers: limits and trial come from their own settings)"
                        onCommit={(next) =>
                          run(`${e.display_name} description saved`, () =>
                            setRatePlanEditionCopy(plan.id, e.plan_key, e.display_name, next.trim() || null))
                        }
                      />
                    </div>
                  ) : e.description ? (
                    <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{e.description}</div>
                  ) : null}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {editable ? (
                    <Cell
                      align="right"
                      value={majorInput(e.monthly_price_minor)}
                      placeholder="0"
                      onCommit={(next) => {
                        const minor = parseMajor(next);
                        if (minor === undefined) return toast('Enter an amount, e.g. 19.99');
                        return saveEdition(e.plan_key, { monthlyPriceMinor: minor }, `${e.display_name} monthly price saved`);
                      }}
                    />
                  ) : (
                    money(e.monthly_price_minor, cur)
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {editable ? (
                    <Cell
                      align="right"
                      value={majorInput(e.annual_price_minor)}
                      placeholder="none"
                      onCommit={(next) => {
                        const minor = parseMajor(next);
                        if (minor === undefined) return toast('Enter an amount, e.g. 199');
                        return saveEdition(
                          e.plan_key,
                          { monthlyPriceMinor: e.monthly_price_minor, annualPriceMinor: minor },
                          `${e.display_name} annual price saved`,
                        );
                      }}
                    />
                  ) : (
                    money(e.annual_price_minor, cur)
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {editable ? (
                    <Cell
                      align="right"
                      width={70}
                      value={e.annual_asset_months == null ? '' : String(e.annual_asset_months)}
                      placeholder="none"
                      onCommit={(next) => {
                        const months = next.trim() === '' ? 0 : parseCount(next);
                        if (months == null || months > 12) return toast('Enter 1 to 12 months, or leave empty');
                        return saveEdition(
                          e.plan_key,
                          { monthlyPriceMinor: e.monthly_price_minor, annualAssetMonths: months },
                          `${e.display_name} annual asset price saved`,
                        );
                      }}
                    />
                  ) : (
                    e.annual_asset_months ?? '—'
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {editable ? (
                    <Cell
                      align="right"
                      width={70}
                      value={e.max_assets == null ? '' : String(e.max_assets)}
                      placeholder="no limit"
                      onCommit={(next) => {
                        const max = next.trim() === '' ? 0 : parseCount(next);
                        if (max == null) return toast('Enter a whole number of assets, or leave empty');
                        return saveEdition(
                          e.plan_key,
                          { monthlyPriceMinor: e.monthly_price_minor, maxAssets: max },
                          `${e.display_name} asset limit saved`,
                        );
                      }}
                    />
                  ) : (
                    e.max_assets ?? '—'
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {editable ? (
                    <Cell
                      align="right"
                      width={70}
                      value={e.trial_days == null ? '' : String(e.trial_days)}
                      placeholder="none"
                      onCommit={(next) => {
                        const days = next.trim() === '' ? 0 : parseCount(next);
                        if (days == null) return toast('Enter a whole number of days, or leave empty');
                        return saveEdition(
                          e.plan_key,
                          { monthlyPriceMinor: e.monthly_price_minor, trialDays: days },
                          `${e.display_name} trial saved`,
                        );
                      }}
                    />
                  ) : (
                    e.trial_days ?? '—'
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {editable ? (
                    <Cell
                      align="right"
                      width={70}
                      value={String(e.included_credits_per_asset)}
                      onCommit={(next) => {
                        const credits = parseCount(next);
                        if (credits == null) return toast('Enter a whole number of credits');
                        return saveEdition(
                          e.plan_key,
                          { monthlyPriceMinor: e.monthly_price_minor, includedCreditsPerAsset: credits },
                          `${e.display_name} credits saved`,
                        );
                      }}
                    />
                  ) : (
                    e.included_credits_per_asset
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {editable ? (
                    <Cell
                      align="right"
                      width={70}
                      value={String(Number(e.asset_rate_multiplier))}
                      onCommit={(next) => {
                        const value = Number(next.trim());
                        if (!Number.isFinite(value) || value <= 0) return toast('Enter a multiplier, e.g. 1 or 1.35');
                        return saveEdition(
                          e.plan_key,
                          { monthlyPriceMinor: e.monthly_price_minor, assetRateMultiplier: value },
                          `${e.display_name} asset rate saved`,
                        );
                      }}
                    />
                  ) : (
                    Number(e.asset_rate_multiplier)
                  )}
                </td>
                <td style={{ textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={e.is_contact_sales}
                    disabled={!editable}
                    style={{ width: 'auto' }}
                    onChange={() =>
                      saveEdition(
                        e.plan_key,
                        { monthlyPriceMinor: e.monthly_price_minor, isContactSales: !e.is_contact_sales },
                        `${e.display_name} updated`,
                      )
                    }
                  />
                </td>
                <td style={{ textAlign: 'center' }}>
                  {/* Radio, not a checkbox: the server clears the others, because
                      two recommended plans is a pricing page nobody proof-read. */}
                  <input
                    type="radio"
                    name={`featured-${plan.id}`}
                    checked={e.is_featured}
                    disabled={!editable || e.is_contact_sales}
                    style={{ width: 'auto' }}
                    onChange={() =>
                      saveEdition(
                        e.plan_key,
                        { monthlyPriceMinor: e.monthly_price_minor, isFeatured: true },
                        `${e.display_name} is now the recommended plan`,
                      )
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );

  // --- Bands, with a worked example from the server --------------------------

  const [newBand, setNewBand] = useState({ min: '', max: '', price: '' });
  const [previewCount, setPreviewCount] = useState('100');
  const [previewEdition, setPreviewEdition] = useState(
    plan.editions.find((e) => e.is_featured)?.plan_key ?? plan.editions[0]?.plan_key ?? '',
  );
  const [preview, setPreview] = useState<AssetChargeLine[] | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    const count = parseCount(previewCount);
    if (count == null || !previewEdition) {
      setPreview(null);
      return;
    }
    let live = true;
    void previewAssetCharge(plan.id, previewEdition, count)
      .then((lines) => { if (live) { setPreview(lines); setPreviewError(null); } })
      .catch((e: Error) => { if (live) { setPreview(null); setPreviewError(e.message); } });
    return () => { live = false; };
    // plan.content_hash so the example re-runs the moment a band changes.
  }, [plan.id, plan.content_hash, previewCount, previewEdition]);

  const previewTotal = useMemo(
    () => (preview ?? []).reduce((sum, line) => sum + line.subtotal_minor, 0),
    [preview],
  );

  const bands = (
    <Section
      title="What assets cost"
      hint="This is what actually bills almost everybody, so it comes first. Each step prices only the assets inside it — a customer with 40 assets pays the first rate for 25 of them and the second rate for the other 15. The last step must be open-ended, or assets above it would be free."
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 1fr) minmax(280px, 1fr)', gap: 20 }}>
        <div>
          <table className="matrix">
            <thead>
              <tr>
                <th>From asset</th><th>To asset</th><th style={{ textAlign: 'right' }}>Each</th><th />
              </tr>
            </thead>
            <tbody>
              {plan.bands.map((b) => (
                <tr key={b.min_assets}>
                  <td>{b.min_assets}</td>
                  <td>{b.max_assets ?? <span className="muted">and above</span>}</td>
                  <td style={{ textAlign: 'right' }}>
                    {editable ? (
                      <Cell
                        align="right"
                        value={majorInput(b.unit_minor)}
                        onCommit={(next) => {
                          const minor = parseMajor(next);
                          if (minor == null) return toast('Enter a price, e.g. 3 or 2.50');
                          return run(`Band from ${b.min_assets} saved`, () =>
                            setRatePlanBand(plan.id, b.min_assets, b.max_assets, minor, b.label));
                        }}
                      />
                    ) : (
                      money(b.unit_minor, cur)
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {editable ? (
                      <button
                        className="btn"
                        style={{ padding: '4px 8px', fontSize: 12 }}
                        onClick={() => run('Band removed', () => removeRatePlanBand(plan.id, b.min_assets))}
                      >
                        Remove
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {editable ? (
            <div className="row" style={{ gap: 8, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                value={newBand.min}
                placeholder="from"
                onChange={(e) => setNewBand((b) => ({ ...b, min: e.target.value }))}
                style={{ width: 80 }}
              />
              <input
                value={newBand.max}
                placeholder="to (blank = open)"
                onChange={(e) => setNewBand((b) => ({ ...b, max: e.target.value }))}
                style={{ width: 150 }}
              />
              <input
                value={newBand.price}
                placeholder={`each (${symbolFor(cur)})`}
                onChange={(e) => setNewBand((b) => ({ ...b, price: e.target.value }))}
                style={{ width: 110 }}
              />
              <button
                className="btn"
                onClick={() => {
                  const min = parseCount(newBand.min);
                  const max = parseCount(newBand.max);
                  const price = parseMajor(newBand.price);
                  if (min == null || min < 1) return toast('A band starts at an asset number, e.g. 51');
                  if (max === undefined) return toast('Leave "to" blank for the open band');
                  if (price == null) return toast('Enter a price for the band');
                  return run('Band added', async () => {
                    await setRatePlanBand(plan.id, min, max, price, null);
                    setNewBand({ min: '', max: '', price: '' });
                  });
                }}
              >
                Add band
              </button>
            </div>
          ) : null}
        </div>

        <div className="card" style={{ boxShadow: 'none', background: 'var(--panel2)' }}>
          <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 13 }}>What that comes to</div>
          <div className="row" style={{ gap: 8, alignItems: 'center', marginBottom: 10 }}>
            <input
              value={previewCount}
              onChange={(e) => setPreviewCount(e.target.value)}
              style={{ width: 80 }}
            />
            <span className="muted" style={{ fontSize: 12 }}>assets on</span>
            <select
              value={previewEdition}
              onChange={(e) => setPreviewEdition(e.target.value)}
              style={{ padding: '6px 8px', fontSize: 13 }}
            >
              {plan.editions.map((e) => (
                <option key={e.plan_key} value={e.plan_key}>{e.display_name}</option>
              ))}
            </select>
          </div>
          {previewError ? (
            <p className="hint" style={{ color: 'var(--danger)' }}>{previewError}</p>
          ) : preview && preview.length ? (
            <>
              <table className="matrix">
                <tbody>
                  {preview.map((line) => (
                    <tr key={line.from_asset}>
                      <td>
                        {line.from_asset}–{line.to_asset}
                        <span className="muted"> × {money(line.unit_minor, cur)}</span>
                      </td>
                      <td style={{ textAlign: 'right' }}>{money(line.subtotal_minor, cur)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td style={{ fontWeight: 700 }}>Assets</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{money(previewTotal, cur)}</td>
                  </tr>
                </tbody>
              </table>
              <p className="hint" style={{ marginTop: 10 }}>
                Computed by the same function that bills, so this cannot drift from the invoice.
                The edition fee is on top.
              </p>
            </>
          ) : (
            <p className="hint">Enter an asset count to see the graduated lines.</p>
          )}
        </div>
      </div>
    </Section>
  );

  // --- Trial -----------------------------------------------------------------

  const trial = plan.trial;
  const trialSection = (
    <Section
      title="Starting out"
      hint="What a brand-new workspace gets. Read by checkout when it subscribes, and by the website. A customer keeps the terms of the price list it signed up under, so changing these never affects anybody already here."
    >
      <div className="row" style={{ gap: 24, flexWrap: 'wrap' }}>
        <div>
          <label style={{ marginTop: 0 }}>Days free</label>
          {editable ? (
            <Cell
              value={String(trial?.trial_days ?? '')}
              onCommit={(next) => {
                const days = parseCount(next);
                if (days == null) return toast('Enter a whole number of days');
                return run('Trial saved', () =>
                  setRatePlanTrial(plan.id, days, trial?.trial_asset_cap ?? null, trial?.free_first_assets ?? 1));
              }}
            />
          ) : (
            <div>{trial?.trial_days ?? '—'}</div>
          )}
        </div>
        <div>
          <label style={{ marginTop: 0 }}>Assets allowed during the trial</label>
          {editable ? (
            <Cell
              value={trial?.trial_asset_cap == null ? '' : String(trial.trial_asset_cap)}
              placeholder="unlimited"
              onCommit={(next) => {
                const cap = parseCount(next);
                if (cap === undefined) return toast('Enter a whole number, or leave blank for unlimited');
                return run('Trial saved', () =>
                  setRatePlanTrial(plan.id, trial?.trial_days ?? 0, cap, trial?.free_first_assets ?? 1));
              }}
            />
          ) : (
            <div>{trial?.trial_asset_cap ?? 'unlimited'}</div>
          )}
        </div>
        <div>
          <label style={{ marginTop: 0 }}>Assets that are always free</label>
          {editable ? (
            <Cell
              value={String(trial?.free_first_assets ?? '')}
              onCommit={(next) => {
                const free = parseCount(next);
                if (free == null) return toast('Enter a whole number');
                return run('Trial saved', () =>
                  setRatePlanTrial(plan.id, trial?.trial_days ?? 0, trial?.trial_asset_cap ?? null, free));
              }}
            />
          ) : (
            <div>{trial?.free_first_assets ?? '—'}</div>
          )}
        </div>
      </div>
    </Section>
  );

  // --- Credit packs ----------------------------------------------------------

  const [newPack, setNewPack] = useState({ sku: '', units: '', price: '' });
  const packs = (
    <Section title="AI credit top-ups" hint="What a customer can buy when the credits included with its plan run out.">
      <table className="matrix">
        <thead>
          <tr>
            <th>Bundle</th><th style={{ textAlign: 'right' }}>Credits</th>
            <th style={{ textAlign: 'right' }}>Price</th>
            <th style={{ textAlign: 'right' }}>Works out at</th><th />
          </tr>
        </thead>
        <tbody>
          {plan.credit_packs.map((k) => (
            <tr key={k.sku}>
              <td>{k.display_name} <span className="muted" style={{ fontSize: 11 }}>{k.sku}</span></td>
              <td style={{ textAlign: 'right' }}>{k.units}</td>
              <td style={{ textAlign: 'right' }}>
                {editable ? (
                  <Cell
                    align="right"
                    value={majorInput(k.price_minor)}
                    onCommit={(next) => {
                      const minor = parseMajor(next);
                      if (minor == null) return toast('Enter a price');
                      return run(`${k.sku} saved`, () =>
                        setRatePlanCreditPack(plan.id, { ...k, price_minor: minor }));
                    }}
                  />
                ) : (
                  money(k.price_minor, cur)
                )}
              </td>
              <td style={{ textAlign: 'right' }} className="muted">
                {money(Math.round(k.price_minor / k.units), cur)}
              </td>
              <td style={{ textAlign: 'right' }}>
                {editable ? (
                  <button
                    className="btn"
                    style={{ padding: '4px 8px', fontSize: 12 }}
                    onClick={() => run('Pack removed', () => removeRatePlanCreditPack(plan.id, k.sku))}
                  >
                    Remove
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {editable ? (
        <div className="row" style={{ gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <input value={newPack.sku} placeholder="sku" style={{ width: 160 }}
            onChange={(e) => setNewPack((p) => ({ ...p, sku: e.target.value }))} />
          <input value={newPack.units} placeholder="credits" style={{ width: 100 }}
            onChange={(e) => setNewPack((p) => ({ ...p, units: e.target.value }))} />
          <input value={newPack.price} placeholder={`price (${symbolFor(cur)})`} style={{ width: 120 }}
            onChange={(e) => setNewPack((p) => ({ ...p, price: e.target.value }))} />
          <button
            className="btn"
            onClick={() => {
              const units = parseCount(newPack.units);
              const price = parseMajor(newPack.price);
              if (!newPack.sku.trim()) return toast('A pack needs a SKU');
              if (units == null || units <= 0) return toast('A pack needs a credit count');
              if (price == null) return toast('A pack needs a price');
              return run('Pack added', async () => {
                await setRatePlanCreditPack(plan.id, {
                  sku: newPack.sku.trim(),
                  display_name: String(units),
                  units,
                  price_minor: price,
                  sort_order: plan.credit_packs.length + 1,
                  is_active: true,
                });
                setNewPack({ sku: '', units: '', price: '' });
              });
            }}
          >
            Add pack
          </button>
        </div>
      ) : null}
    </Section>
  );

  // --- AI action prices ------------------------------------------------------

  const [showAllCosts, setShowAllCosts] = useState(false);
  const published = plan.credit_costs.filter((c) => c.public_key);
  const unpublished = plan.credit_costs.filter((c) => !c.public_key);

  const costRow = (c: (typeof plan.credit_costs)[number]) => (
    <tr key={c.action_key}>
      <td>
        <span className="feat-key">{c.action_key}</span>
        {c.display_label ? <div className="muted" style={{ fontSize: 11 }}>{c.display_label}</div> : null}
      </td>
      <td style={{ textAlign: 'right' }}>
        {editable ? (
          <Cell
            align="right"
            width={60}
            value={String(c.credits)}
            onCommit={(next) => {
              const credits = parseCount(next);
              if (credits == null) return toast('Enter a whole number of credits');
              return run(`${c.action_key} priced`, () =>
                setRatePlanCreditCost(plan.id, c.action_key, credits, c.display_label, c.public_key, c.public_sort_order));
            }}
          />
        ) : (
          c.credits
        )}
      </td>
      <td>
        {editable ? (
          <Cell
            width={150}
            value={c.public_key ?? ''}
            placeholder="not listed"
            onCommit={(next) => {
              const key = next.trim();
              if (key === '') {
                return run(`${c.action_key} taken off the pricing page`, () =>
                  unpublishRatePlanCreditCost(plan.id, c.action_key));
              }
              return run(`${c.action_key} listed as ${key}`, () =>
                setRatePlanCreditCost(plan.id, c.action_key, c.credits, c.display_label, key,
                  c.public_sort_order ?? published.length + 1));
            }}
          />
        ) : (
          c.public_key ?? <span className="muted">not listed</span>
        )}
      </td>
      <td style={{ textAlign: 'right' }}>
        {editable ? (
          <Cell
            align="right"
            width={56}
            value={c.public_sort_order == null ? '' : String(c.public_sort_order)}
            onCommit={(next) => {
              const order = parseCount(next);
              if (order === undefined) return toast('Enter a whole number');
              return run('Order saved', () =>
                setRatePlanCreditCost(plan.id, c.action_key, c.credits, c.display_label, c.public_key, order));
            }}
          />
        ) : (
          c.public_sort_order ?? <span className="muted">—</span>
        )}
      </td>
    </tr>
  );

  const costs = (
    <Section
      title="What each AI action costs"
      hint="How many credits a customer spends each time the app does one of these. Filling in “shown on the website as” puts the action on the public pricing page under that name — the website has to carry wording for it, or its build stops."
    >
      <table className="matrix">
        <thead>
          <tr>
            <th>Action</th>
            <th style={{ textAlign: 'right' }}>Credits</th>
            <th>Shown on the website as</th>
            <th style={{ textAlign: 'right' }}>Order</th>
          </tr>
        </thead>
        <tbody>
          {published.map(costRow)}
          {showAllCosts ? unpublished.map(costRow) : null}
        </tbody>
      </table>
      <button
        className="btn"
        style={{ marginTop: 12 }}
        onClick={() => setShowAllCosts((v) => !v)}
      >
        {showAllCosts
          ? 'Hide the actions that are charged but not advertised'
          : `Show ${unpublished.length} more that are charged but not advertised`}
      </button>
    </Section>
  );

  // Assets first: that is what bills almost every customer. Plans came first
  // for years because the schema names them first, which is exactly the habit
  // this screen was reorganised to break.
  return (
    <>
      {bands}
      {editions}
      {trialSection}
      {packs}
      <RatePlanAddons plan={plan} toast={toast} />
      {costs}
    </>
  );
}
